import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import FormGroup from "@material-ui/core/FormGroup";
import Checkbox from "@material-ui/core/Checkbox";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";
import { IconButton, InputAdornment } from "@material-ui/core";
import { Colorize } from "@material-ui/icons";

const WEEKDAYS = [
	{ value: 1, labelKey: "monday" },
	{ value: 2, labelKey: "tuesday" },
	{ value: 3, labelKey: "wednesday" },
	{ value: 4, labelKey: "thursday" },
	{ value: 5, labelKey: "friday" },
	{ value: 6, labelKey: "saturday" },
	{ value: 0, labelKey: "sunday" },
];

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},
	scheduleSection: {
		marginTop: theme.spacing(2),
		width: "100%",
	},
	scheduleRow: {
		display: "flex",
		gap: theme.spacing(1),
		flexWrap: "wrap",
	},
	weekdaysGroup: {
		display: "flex",
		flexWrap: "wrap",
		marginTop: theme.spacing(1),
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	colorAdorment: {
		width: 20,
		height: 20,
	},
}));

const QueueSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	color: Yup.string().min(3, "Too Short!").max(9, "Too Long!").required(),
	greetingMessage: Yup.string(),
	scheduleEnabled: Yup.boolean(),
	scheduleStartTime: Yup.string().when("scheduleEnabled", {
		is: true,
		then: schema => schema.required("Required"),
	}),
	scheduleEndTime: Yup.string().when("scheduleEnabled", {
		is: true,
		then: schema => schema.required("Required"),
	}),
	scheduleWeekdays: Yup.array().when("scheduleEnabled", {
		is: true,
		then: schema => schema.min(1, "Required"),
	}),
});

const QueueModal = ({ open, onClose, queueId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		color: "",
		greetingMessage: "",
		scheduleEnabled: false,
		scheduleStartTime: "08:00",
		scheduleEndTime: "18:00",
		scheduleWeekdays: [1, 2, 3, 4, 5],
		scheduleTimezone: "America/Sao_Paulo",
	};

	const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
	const [queue, setQueue] = useState(initialState);
	const greetingRef = useRef();

	useEffect(() => {
		(async () => {
			if (!queueId) return;
			try {
				const { data } = await api.get(`/queue/${queueId}`);
				setQueue(prevState => {
					return {
						...prevState,
						...data,
						scheduleWeekdays: Array.isArray(data.scheduleWeekdays)
							? data.scheduleWeekdays
							: [1, 2, 3, 4, 5],
					};
				});
			} catch (err) {
				toastError(err);
			}
		})();

		return () => {
			setQueue(initialState);
		};
	}, [queueId, open]);

	const handleClose = () => {
		onClose();
		setQueue(initialState);
	};

	const handleSaveQueue = async values => {
		try {
			if (queueId) {
				await api.put(`/queue/${queueId}`, values);
			} else {
				await api.post("/queue", values);
			}
			toast.success(i18n.t("queueModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const toggleWeekday = (values, setFieldValue, dayValue) => {
		const current = values.scheduleWeekdays || [];
		const next = current.includes(dayValue)
			? current.filter(day => day !== dayValue)
			: [...current, dayValue].sort((a, b) => {
					const order = day => (day === 0 ? 7 : day);
					return order(a) - order(b);
			  });
		setFieldValue("scheduleWeekdays", next);
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
				<DialogTitle>
					{queueId
						? `${i18n.t("queueModal.title.edit")}`
						: `${i18n.t("queueModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={queue}
					enableReinitialize={true}
					validationSchema={QueueSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveQueue(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<Field
									as={TextField}
									label={i18n.t("queueModal.form.name")}
									autoFocus
									name="name"
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									fullWidth
									className={classes.textField}
								/>
								<Field
									as={TextField}
									label={i18n.t("queueModal.form.color")}
									name="color"
									id="color"
									onFocus={() => {
										setColorPickerModalOpen(true);
										greetingRef.current.focus();
									}}
									error={touched.color && Boolean(errors.color)}
									helperText={touched.color && errors.color}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<div
													style={{ backgroundColor: values.color }}
													className={classes.colorAdorment}
												></div>
											</InputAdornment>
										),
										endAdornment: (
											<IconButton
												size="small"
												color="default"
												onClick={() => setColorPickerModalOpen(true)}
											>
												<Colorize />
											</IconButton>
										),
									}}
									variant="outlined"
									margin="dense"
									fullWidth
								/>
								<ColorPicker
									open={colorPickerModalOpen}
									handleClose={() => setColorPickerModalOpen(false)}
									onChange={color => {
										values.color = color;
										setQueue(() => {
											return { ...values, color };
										});
									}}
								/>
								<div>
									<Field
										as={TextField}
										label={i18n.t("queueModal.form.greetingMessage")}
										type="greetingMessage"
										multiline
										inputRef={greetingRef}
										rows={5}
										fullWidth
										name="greetingMessage"
										error={
											touched.greetingMessage && Boolean(errors.greetingMessage)
										}
										helperText={
											touched.greetingMessage && errors.greetingMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>

								<div className={classes.scheduleSection}>
									<FormControl component="fieldset" fullWidth>
										<FormControlLabel
											control={
												<Switch
													checked={Boolean(values.scheduleEnabled)}
													onChange={event =>
														setFieldValue("scheduleEnabled", event.target.checked)
													}
													color="primary"
												/>
											}
											label={i18n.t("queueModal.form.scheduleEnabled")}
										/>
										<Typography variant="caption" color="textSecondary">
											{i18n.t("queueModal.form.scheduleHint")}
										</Typography>

										{values.scheduleEnabled && (
											<>
												<div className={classes.scheduleRow}>
													<Field
														as={TextField}
														label={i18n.t("queueModal.form.scheduleStartTime")}
														name="scheduleStartTime"
														type="time"
														InputLabelProps={{ shrink: true }}
														error={
															touched.scheduleStartTime &&
															Boolean(errors.scheduleStartTime)
														}
														helperText={
															touched.scheduleStartTime && errors.scheduleStartTime
														}
														variant="outlined"
														margin="dense"
														className={classes.textField}
													/>
													<Field
														as={TextField}
														label={i18n.t("queueModal.form.scheduleEndTime")}
														name="scheduleEndTime"
														type="time"
														InputLabelProps={{ shrink: true }}
														error={
															touched.scheduleEndTime &&
															Boolean(errors.scheduleEndTime)
														}
														helperText={
															touched.scheduleEndTime && errors.scheduleEndTime
														}
														variant="outlined"
														margin="dense"
														className={classes.textField}
													/>
												</div>

												<Typography variant="subtitle2" style={{ marginTop: 8 }}>
													{i18n.t("queueModal.form.scheduleWeekdays")}
												</Typography>
												<FormGroup row className={classes.weekdaysGroup}>
													{WEEKDAYS.map(day => (
														<FormControlLabel
															key={day.value}
															control={
																<Checkbox
																	checked={(values.scheduleWeekdays || []).includes(
																		day.value
																	)}
																	onChange={() =>
																		toggleWeekday(values, setFieldValue, day.value)
																	}
																	color="primary"
																/>
															}
															label={i18n.t(`queueModal.weekdays.${day.labelKey}`)}
														/>
													))}
												</FormGroup>
												{touched.scheduleWeekdays && errors.scheduleWeekdays && (
													<Typography variant="caption" color="error">
														{errors.scheduleWeekdays}
													</Typography>
												)}
											</>
										)}
									</FormControl>
								</div>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("queueModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{queueId
										? `${i18n.t("queueModal.buttons.okEdit")}`
										: `${i18n.t("queueModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default QueueModal;
